import { UserRole } from '@repo/common/enums/UserRole';
import { pickColor } from '@repo/common/utils';
import { createMockableHook } from '@repo/common/utils/createMockableHook';
import { isNil, lowerCase, map } from 'lodash-es';
import { useMemo } from 'react';
import type { IUser, IWorkspace } from '../../types';
import { useCurrentUser } from '../user';
import { useWorkspace } from '../workspace';

const ADMIN_ROLES = map([UserRole.ADMIN], lowerCase);
const EDITOR_ROLES = map([UserRole.EDITOR, UserRole.ADMIN], lowerCase);
const VIEWER_ROLES = map([UserRole.VIEWER, UserRole.GUEST], lowerCase);

export function useMaybeAuthUser() {
	const { data: currentUser } = useCurrentUser();
	return {
		user: currentUser as IUser | undefined,
		workspace: currentUser?.workspace as IWorkspace | undefined,
	};
}

/**
 * Custom hook to retrieve the authenticated user's information and access privileges.
 * @returns An object containing user information and access privileges.
 * @throws {Error} If the user is not loaded.
 */
function useAuthUserInternal(): {
	domain: string | undefined;
	workspace: IWorkspace;
	user: IUser;
	color: string;
	isAdminUser: boolean;
	isEditorUser: boolean;
	isViewerUser: boolean;
	displayName: string;
	isGuestUser: boolean;
	isNonAuthenticatedUser: boolean;
	isEditorOrAdminUser: boolean;
	isViewerOrGuestUser: boolean;
	refetch: () => void;
	isLoading: boolean;
} {
	const { data: currentUser, isLoading, refetch } = useCurrentUser();
	const { workspace } = useWorkspace();

	return useMemo(() => {
		const role: string | undefined = currentUser?.role?.toLowerCase();

		const isNonAuthenticatedUser: boolean =
			isNil(currentUser) || currentUser?.email === 'AnonymousUser';
		const isAdminUser: boolean = !!(role && ADMIN_ROLES.includes(role));
		const isEditorUser: boolean = !!(role && EDITOR_ROLES.includes(role));
		const isViewerUser: boolean = !!(role && VIEWER_ROLES.includes(role));
		const isGuestUser: boolean = !!(role === UserRole.GUEST.toLowerCase());

		const displayName =
			currentUser?.display_name?.trim() ?? currentUser?.email ?? 'User';

		return {
			refetch,
			/**
			 * The current workspace actively associated with the user.
			 */
			workspace: (workspace ?? {}) as IWorkspace,
			/**
			 * The authenticated user.
			 */
			user: (currentUser ?? {}) as IUser,
			/**
			 * The color associated with the user.
			 */
			color: pickColor(displayName),
			/**
			 * Indicates if the user is an admin user.
			 */
			isAdminUser,
			/**
			 * Indicates if the user is an editor user.
			 */
			isEditorUser,
			/**
			 * Indicates if the user is a viewer user.
			 */
			isViewerUser,
			/**
			 * The display name of the user.
			 */
			displayName,
			domain: currentUser?.email?.split('@')?.[1] ?? '',
			/**
			 * Indicates if the user is a guest user.
			 */
			isGuestUser,
			/**
			 * Indicates if the user is an anonymous user.
			 */
			isNonAuthenticatedUser,
			isEditorOrAdminUser: isEditorUser || isAdminUser,
			isViewerOrGuestUser: isViewerUser || isGuestUser,
			isLoading,
		};
	}, [currentUser, refetch, isLoading, workspace]);
}

export const [useAuthUser, MockUseAuthUserProvider] =
	createMockableHook(useAuthUserInternal);
